import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import CaseList from "../CaseList";
import CaseNavList from "../CaseNavList";
import CaseForm from "../CaseForm";
import {
  getAssetDetailById,
  getAssetListItems,
} from "../../../../redux/Case/assetSlice";
import { ASSET_CASE_TYPE_MAP, ASSET_CASE_TYPE_REVERSE_MAP } from "./assetSubTypeConfig";
import { normalizeCaseType, isCurrentStatus, getItemStatus } from "../../../../shared/caseActiveStatus";
import { TableListLoader } from "../../../../components/Loader/TableListLoader";
import { FormLoader } from "../../../../components/Loader/FormLoader";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import { Toaster } from "@fluentui/react-components";

interface AssetDetailPageProps {
  tab?: string;
}

const AssetDetailPage: React.FC<AssetDetailPageProps> = ({ tab }) => {
  const { clientId, case_type, action, caseId } = useParams();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { showToast, toasterId } = useToastMessage();
  const [currentRecord, setCurrentRecord] = useState(1);
  const [showNonCurrent, setShowNonCurrent] = useState(false);
  // Tracks whether auto-switch to non-current has already fired for this case_type.
  // Prevents the toggle reverting immediately when user clicks back to current.
  const hasAutoSwitched = useRef(false);
  // True once we know non-current records exist (set when auto-switch fires).
  const [nonCurrentExists, setNonCurrentExists] = useState(false);

  const { activeItem, listItems, loading, activeItemloading } = useSelector(
    (state: RootState) => state.assets
  );

  // When on assets/:caseId/asset/:action, case_type param is absent — derive slug from activeItem
  const caseType = case_type || ASSET_CASE_TYPE_REVERSE_MAP[activeItem?.case_type || ""] || "";
  const dbCaseType = ASSET_CASE_TYPE_MAP[caseType] || activeItem?.case_type;

  // Reset to the default (current-only) view whenever the sub case type changes
  useEffect(() => {
    setShowNonCurrent(false);
    setNonCurrentExists(false);
    hasAutoSwitched.current = false;
  }, [case_type]);

  useEffect(() => {
    if (action && action?.length > 5) {
      navigate(
        `/clients/${clientId}/assets/${case_type}/${action}/${tab || "asset"}/view`
      );
    }
    if (!action && caseId) {
      navigate(
        `/clients/${clientId}/assets/${case_type}/${caseId}/${tab || "asset"}/view`
      );
    }
  }, [action, caseId]);

  useEffect(() => {
    if (clientId) {
      dispatch(getAssetListItems({ customerId: clientId, caseType: dbCaseType }))
        .then((result: any) => {
          if (!showNonCurrent && !hasAutoSwitched.current) {
            const items = Array.isArray(result?.payload) ? result.payload : [];
            const byType = dbCaseType
              ? items.filter((x: any) => normalizeCaseType(x.case_type) === normalizeCaseType(dbCaseType))
              : items;
            const hasCurrentRecords = byType.some((x: any) => isCurrentStatus("asset", getItemStatus(x)));
            if (!hasCurrentRecords && byType.length > 0) {
              hasAutoSwitched.current = true;
              setNonCurrentExists(true);
              setShowNonCurrent(true);
            }
          }
        });
    }
  }, [clientId, dbCaseType, dispatch]);

  useEffect(() => {
    if (caseId && clientId) {
      dispatch(getAssetDetailById({ id: caseId, customerId: clientId }));
    }
  }, [caseId, clientId, dispatch]);

  const caseTypeListItems = useMemo(() => {
    const byType = dbCaseType
      ? listItems.filter((item: any) => normalizeCaseType(item.case_type) === normalizeCaseType(dbCaseType))
      : listItems;
    return !showNonCurrent
      ? byType.filter((item: any) => isCurrentStatus("asset", getItemStatus(item)))
      : byType;
  }, [listItems, dbCaseType, showNonCurrent]);

  // User toggled back to current view but no current records exist.
  // In this state: show empty table + Add New, hide the form.
  const isEmptyCurrentView =
    nonCurrentExists && !showNonCurrent && caseTypeListItems.length === 0;
  // isEmptyCurrentView must never block Add New itself -- "add"/"edit" don't
  // depend on any existing record for the active toggle. Without this
  // exemption, creating a case then editing its status to a non-current one
  // (auto-switching the toggle to Non-current), then toggling back to
  // Current Cases and clicking Add New left isEmptyCurrentView true for the
  // "add" route too, and the Add New form silently failed to render.
  const canRenderForm =
    !isEmptyCurrentView || ["add", "edit"].includes(action || "");

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      {action && (
        <CaseNavList
          menuItem={undefined}
          section="assets"
          tab={tab}
          caseType={caseType}
          caseDetail={activeItem}
          mode={action || ""}
          isEmptyCurrentView={isEmptyCurrentView}
        />
      )}
      <div className="case_container_flex">
        {action && canRenderForm &&
          (activeItemloading ? (
            <FormLoader />
          ) : (
            <CaseForm
              currentRecord={currentRecord}
              setCurrentRecord={setCurrentRecord}
              mode={action || ""}
              section="assets"
              caseType={caseType}
              caseDetail={activeItem}
              caseItemList={caseTypeListItems}
              tab={tab}
              notify={showToast}
            />
          ))}
        <div>
          {loading ? (
            <TableListLoader rows={8} columns={6} />
          ) : (
            action &&
            (caseTypeListItems.length > 0 || nonCurrentExists) && (
              <CaseList
                currentRecord={currentRecord}
                setCurrentRecord={setCurrentRecord}
                module="assets"
                data={caseTypeListItems}
                activeItem={activeItem}
                section="assets"
                tab={tab}
                showNonCurrent={showNonCurrent}
                onToggleNonCurrent={() => setShowNonCurrent((prev) => !prev)}
                emptyMessage={
                  isEmptyCurrentView
                    ? 'No active records for current cases. Click "Non-current Cases" to view non-current records.'
                    : undefined
                }
              />
            )
          )}
        </div>
      </div>
    </>
  );
};

export default AssetDetailPage;
